import React, { useMemo, useState } from "react";
import { useAppSettings } from "../../lib/AppSettingsContext";
import { CustomIcon } from "../shared/CustomIcon";
import { apiRerouteAppointment, apiClaimAppointment, apiAddAssistantNote, apiSetAppointmentStatus } from "./api";

// ============================================================
// AdminTasksList — the pastor / assistant / admin view of incoming
// appointment requests. Renders:
//   • Crisis requests pinned to the top with a red ⚠️ badge (never
//     reroutable)
//   • Pending requests with a Reroute-to-Assistant action for the
//     pastor and a Claim/Add-note action for routed-to assistants
//   • Confirmed section underneath
// ============================================================
const COMM_LABELS = { "in-person": "In-person", phone: "Phone Call", video: "Video Call" };

export default function AdminTasksList({
  currentUser, isSuperAdmin, appointments, setAppointments, users, pastorAssistantsById,
}) {
  const { settings } = useAppSettings();
  const TYPE_LABELS = {
    short: settings.apptShortName || "On-the-Fly Connections",
    long:  settings.apptLongName  || "Weekday Connections",
  };
  const uid = currentUser.id;
  const isAdmin = isSuperAdmin || currentUser.role === "limited_admin";
  const isPastor = currentUser.role === "pastor";

  const visible = useMemo(() => {
    return appointments.filter(a => {
      if (a.status === "cancelled") return false;
      if (isAdmin) return true; // admins see everything
      if (a.pastorId === uid) return true;
      const routed = a.routedToAssistants || [];
      return routed.includes(uid);
    });
  }, [appointments, uid, isAdmin]);

  const buckets = useMemo(() => {
    const crisis = [], pending = [], confirmed = [];
    visible.forEach(a => {
      if (a.isCrisis) crisis.push(a);
      else if (a.status === "confirmed") confirmed.push(a);
      else pending.push(a);
    });
    const byDate = (x, y) => (x.date || "").localeCompare(y.date || "");
    crisis.sort(byDate); pending.sort(byDate); confirmed.sort(byDate);
    return { crisis, pending, confirmed };
  }, [visible]);

  const [rerouteFor, setRerouteFor] = useState(null);     // appointment being rerouted
  const [openId, setOpenId] = useState(null);             // request whose detail panel is open
  const [pendingNote, setPendingNote] = useState({});     // { [apptId]: text }

  const replace = (updated) => setAppointments(prev => prev.map(x => x.id === updated.id ? updated : x));

  const onClaim = async (a) => {
    try { replace(await apiClaimAppointment(a.id)); }
    catch (e) { alert(e.message || "Could not claim"); }
  };

  const onAddNote = async (a) => {
    const text = (pendingNote[a.id] || "").trim();
    if (!text) { alert("Please add a note before closing this request"); return; }
    try {
      const updated = await apiAddAssistantNote(a.id, text);
      replace(updated);
      setPendingNote(p => ({ ...p, [a.id]: "" }));
    } catch (e) { alert(e.message || "Could not save note"); }
  };

  const onConfirm = async (a) => {
    try {
      // If a claimed assistant is confirming, require they have at least one note first.
      if (a.claimedByAssistantId === uid) {
        const hasNote = Array.isArray(a.assistantNotes) && a.assistantNotes.some(n => n.userId === uid);
        if (!hasNote && !(pendingNote[a.id] || "").trim()) {
          alert("Please add a note before closing this request");
          return;
        }
        if ((pendingNote[a.id] || "").trim()) {
          await apiAddAssistantNote(a.id, pendingNote[a.id].trim());
        }
      }
      const updated = await apiSetAppointmentStatus(a.id, "confirmed");
      replace(updated);
    } catch (e) { alert(e.message || "Could not confirm"); }
  };

  const onSubmitReroute = async (assistantIds, notesVisibleToAssistant) => {
    if (!rerouteFor) return;
    try {
      const updated = await apiRerouteAppointment(rerouteFor.id, { assistantIds, notesVisibleToAssistant });
      replace(updated);
      setRerouteFor(null);
    } catch (e) { alert(e.message || "Could not reroute"); }
  };

  if (visible.length === 0) {
    return (
      <div className="widget-empty-add" data-testid="admin-tasks-empty">
        <div className="wea-icon"><CustomIcon name="download-inbox" size={40} /></div>
        <div className="wea-title">No incoming requests</div>
        <div className="wea-sub">Public bookings and rerouted appointments will appear here.</div>
      </div>
    );
  }

  const renderCard = (a, section) => (
    <RequestCard
      key={a.id}
      a={a}
      section={section}
      currentUser={currentUser}
      isAdmin={isAdmin}
      isPastor={isPastor}
      users={users}
      open={openId === a.id}
      onOpen={() => setOpenId(openId === a.id ? null : a.id)}
      onReroute={() => setRerouteFor(a)}
      onClaim={() => onClaim(a)}
      onConfirm={() => onConfirm(a)}
      pendingNote={pendingNote[a.id] || ""}
      onPendingNote={(t) => setPendingNote(p => ({ ...p, [a.id]: t }))}
      onAddNote={() => onAddNote(a)}
      typeLabels={TYPE_LABELS}
    />
  );

  return (
    <div data-testid="admin-tasks-list">
      {buckets.crisis.length > 0 && (
        <Section title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><CustomIcon name="warning" size={16} /> Crisis requests</span>} testId="admin-tasks-crisis-section">
          {buckets.crisis.map(a => renderCard(a, "crisis"))}
        </Section>
      )}
      <Section title="Incoming requests" testId="admin-tasks-pending-section">
        {buckets.pending.length === 0 ? <Empty>None pending.</Empty> : buckets.pending.map(a => renderCard(a, "pending"))}
      </Section>
      <Section title="Confirmed appointments" testId="admin-tasks-confirmed-section">
        {buckets.confirmed.length === 0 ? <Empty>None confirmed.</Empty> : buckets.confirmed.map(a => renderCard(a, "confirmed"))}
      </Section>

      {rerouteFor && (
        <RerouteModal
          appt={rerouteFor}
          assistants={pastorAssistantsById?.[rerouteFor.pastorId] || []}
          onClose={() => setRerouteFor(null)}
          onSubmit={onSubmitReroute}
        />
      )}
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <div data-testid={testId} style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, fontWeight: 700, color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.08em", margin: "6px 0 8px" }}>{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return <div style={{ fontSize: 12, color: "var(--kc-meta)", padding: "8px 4px" }}>{children}</div>;
}

function RequestCard({ a, section, currentUser, isAdmin, isPastor, users, open, onOpen, onReroute, onClaim, onConfirm, pendingNote, onPendingNote, onAddNote, typeLabels }) {
  const uid = currentUser.id;
  const isInvitedPastor = a.pastorId === uid;
  const routed = a.routedToAssistants || [];
  const claimed = a.claimedByAssistantId || "";
  const isAssistant = routed.includes(uid);
  const isOwner = claimed === uid;
  const isOtherAssistant = isAssistant && claimed && !isOwner;
  const apptName = (typeLabels && typeLabels[a.apptType]) || a.title;
  const commLabel = COMM_LABELS[a.communicationPref] || a.communicationPref || "—";
  const claimedName = claimed
    ? (users.find(u => u.id === claimed)?.name || a.claimedByAssistantName || "Assistant")
    : "";

  // What does THIS user see in the notes field?
  const canSeeFullNotes = isAdmin || isInvitedPastor || isOwner;
  const showNotesPlaceholder = !canSeeFullNotes && !a.notesVisibleToAssistant && isAssistant;

  return (
    <div data-testid={`admin-task-card-${a.id}`} className="card" style={{ padding: 14, marginBottom: 10, borderLeft: a.isCrisis ? "4px solid #ff6b6b" : (section === "confirmed" ? "4px solid #4ade80" : "4px solid var(--kc-accent)") }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {a.isCrisis && <span data-testid={`crisis-badge-${a.id}`} style={{ background: "#ff6b6b", color: "#1A1A1A", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, letterSpacing: "0.06em", display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="warning" size={10} /> CRISIS</span>}
            <div style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, color: "var(--kc-text)", fontSize: 14, textTransform: "uppercase" }}>{a.userName || "Unknown"}</div>
            <span className={`appt-status ${a.status}`}>{a.status}</span>
            {claimed && <span data-testid={`claimed-by-${a.id}`} style={{ fontSize: 10, color: "var(--kc-accent)", background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)", padding: "2px 8px", borderRadius: 999, border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)" }}>Claimed by {claimedName}</span>}
          </div>
          <div style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 3 }}>
            {apptName}{a.date ? ` · ${a.date}` : ""}{a.time ? ` · ${a.time}` : ""}{a.communicationPref ? ` · ${commLabel}` : ""}
          </div>
        </div>
        <button type="button" className="btn btn-sm btn-outline" data-testid={`admin-task-open-${a.id}`} disabled={isOtherAssistant} onClick={onOpen}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && !isOtherAssistant && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Row label="Type">{apptName}</Row>
          <Row label="When">{a.date || "—"} {a.time ? `· ${a.time}` : ""}</Row>
          <Row label="Subject">{a.title || "—"}</Row>
          <Row label="Communication">{commLabel}</Row>
          {(a.visitorEmail || a.visitorPhone) && <Row label="Contact">{a.visitorEmail || ""}{a.visitorEmail && a.visitorPhone ? " · " : ""}{a.visitorPhone || ""}</Row>}
          <Row label="Notes" testId={`admin-task-notes-${a.id}`}>
            {showNotesPlaceholder ? <em style={{ color: "var(--kc-meta)" }}>Notes hidden by pastor</em> : (a.reason || <em style={{ color: "var(--kc-meta)" }}>None provided</em>)}
          </Row>

          {Array.isArray(a.assistantNotes) && a.assistantNotes.length > 0 && canSeeFullNotes && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: "var(--kc-sub)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>Assistant notes</div>
              {a.assistantNotes.map(n => (
                <div key={n.id} data-testid={`assistant-note-${n.id}`} style={{ fontSize: 12, padding: "6px 8px", background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 6, marginBottom: 4 }}>
                  <div style={{ color: "var(--kc-accent)", fontWeight: 700 }}>{n.name}</div>
                  <div style={{ color: "var(--kc-sub)" }}>{n.text}</div>
                  <div style={{ color: "var(--kc-meta)", fontSize: 10 }}>{n.createdAt}</div>
                </div>
              ))}
            </div>
          )}

          {/* Owner-assistant note input */}
          {isOwner && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", fontSize: 11, color: "var(--kc-sub)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>Assistant note (required)</label>
              <textarea data-testid={`admin-task-assistant-note-input-${a.id}`} rows={2} value={pendingNote} onChange={e => onPendingNote(e.target.value)} placeholder="What did you discuss? What's the next step?" style={{ width: "100%" }} />
              <div style={{ marginTop: 6 }}>
                <button type="button" className="btn btn-sm btn-outline" data-testid={`admin-task-save-note-${a.id}`} onClick={onAddNote}>Save Note</button>
              </div>
            </div>
          )}

          {/* Action row */}
          <div className="flex gap-8 flex-wrap" style={{ marginTop: 12 }}>
            {/* Reroute (pastor only, never on crisis) */}
            {(isInvitedPastor || isAdmin) && !a.isCrisis && section !== "confirmed" && (
              <button type="button" className="btn btn-sm btn-outline" data-testid={`admin-task-reroute-${a.id}`} onClick={onReroute}>
                Reroute to Assistant
              </button>
            )}
            {/* Claim (only routed-to assistants, only if not yet claimed) */}
            {isAssistant && !claimed && (
              <button type="button" className="btn btn-sm btn-gold" data-testid={`admin-task-claim-${a.id}`} onClick={onClaim}>
                Claim This Request
              </button>
            )}
            {/* Confirm — pastor / owner-assistant / admin only */}
            {(isInvitedPastor || isOwner || isAdmin) && a.status !== "confirmed" && (
              <button type="button" className="btn btn-sm btn-gold" data-testid={`admin-task-confirm-${a.id}`} onClick={onConfirm}>
                Confirm
              </button>
            )}
          </div>
        </div>
      )}

      {isOtherAssistant && open && (
        <div data-testid={`admin-task-locked-${a.id}`} style={{ marginTop: 10, fontSize: 12, color: "var(--kc-sub)", fontStyle: "italic", display: "flex", alignItems: "center", gap: 6 }}>
          <CustomIcon name="lock" size={12} /> Claimed by {claimedName}. You can&apos;t view full details.
        </div>
      )}
    </div>
  );
}

function Row({ label, children, testId }) {
  return (
    <div data-testid={testId} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 13 }}>
      <span style={{ width: 110, color: "var(--kc-meta)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ color: "var(--kc-text)", flex: 1, minWidth: 0, wordBreak: "break-word" }}>{children}</span>
    </div>
  );
}

function RerouteModal({ appt, assistants, onClose, onSubmit }) {
  const [selected, setSelected] = useState([]);
  const [allowNotes, setAllowNotes] = useState(false);
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const canSubmit = selected.length > 0;
  return (
    <div className="modal-overlay" data-testid="reroute-modal" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal cal-modal-scrollable" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">Reroute to Assistant</div>
          <button type="button" className="modal-close" data-testid="reroute-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: "var(--kc-sub)", marginBottom: 10 }}>Select one or more assistants. The first to claim it becomes the owner.</p>
          {assistants.length === 0 ? (
            <div className="widget-empty-add" data-testid="reroute-no-assistants">
              <div className="wea-icon"><CustomIcon name="person" size={40} /></div>
              <div className="wea-title">No assistants assigned</div>
              <div className="wea-sub">Ask an admin to add assistants on the Pastors Directory page.</div>
            </div>
          ) : (
            <div className="flex gap-8 flex-wrap" style={{ marginBottom: 12 }}>
              {assistants.map(a => {
                const on = selected.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`reroute-assistant-${a.id}`}
                    onClick={() => toggle(a.id)}
                    style={{
                      padding: "8px 14px", borderRadius: 999,
                      background: on ? "var(--kc-accent)" : "transparent",
                      color: on ? "var(--kc-on-accent)" : "var(--kc-sub)",
                      border: on ? "1px solid var(--kc-accent)" : "1px solid rgba(255,255,255,0.20)",
                      fontWeight: 700, fontSize: 12, cursor: "pointer",
                    }}
                  >{a.name || a.username || a.id}</button>
                );
              })}
            </div>
          )}
          <label data-testid="reroute-allow-notes" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--kc-sub)", cursor: "pointer" }}>
            <input type="checkbox" checked={allowNotes} onChange={e => setAllowNotes(e.target.checked)} />
            Allow assistant to view notes
          </label>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" data-testid="reroute-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-gold" data-testid="reroute-submit" disabled={!canSubmit} onClick={() => onSubmit(selected, allowNotes)}>
            Reroute
          </button>
        </div>
      </div>
    </div>
  );
}
